import { loadEvents } from './loadEvents';
import { GetFolderIdsData } from '../store/schema/GetFolderIdsData';
import { areFolderIdsDifferent } from '../utils/areFolderIdsDifferent';
import { isDateRangeDifferent } from '../utils/isDateRangeDifferent';
import { DateRange } from 'owa-datetime-utils';
import { orchestrator } from 'satcheljs';
import {
    initializeCalendarEventsLoader,
    calendarEventsLoaderInitialized,
} from '../actions/publicActions';
import {
    updateLoadedFolderIds,
    updateLoadedDateRange,
    updateLoadState,
    initializeScenario,
    updateIsInitializingCalendarEventsLoader,
} from '../actions/internalActions';
import {
    calendarCacheInitialized,
    initializeCalendarsCache,
    CalendarCacheScenarioId,
} from 'owa-calendar-cache';
import {
    getLoadState,
    isInitializingCalendarEventsLoader,
} from '../selectors/calendarEventsLoaderStoreSelectors';
import {
    getLoadedFolderIds,
    getLoadedDateRange,
} from '../selectors/calendarEventsLoaderStoreSelectors';
import { getFolderIds } from '../selectors/getFolderIds';
import { updateFolderIdsInCalendarCache } from './updateFolderIdsInCalendarCache';

interface InitValues {
    dateRange: DateRange;
    data: GetFolderIdsData;
}

const initScenarioMap = new Map<CalendarCacheScenarioId, InitValues>();

export const initializeCalendarEventsLoaderOrchestrator = orchestrator(
    initializeCalendarEventsLoader,
    actionMessage => {
        const { scenarioId, getFolderIdsData, dateRange } = actionMessage;
        if (getLoadState(scenarioId) === 'DoesNotExist') {
            /**
             * if the scenario does not exist in our store trigger a initializeScenario action
             * to initialize it in our store and trigger the initializeCalendarsCache action to begin the
             * initialization of the events data in the store.
             **/
            initializeScenario(scenarioId);
        } else {
            if (isInitializingCalendarEventsLoader(scenarioId)) {
                // if the scenario is already initializing, do nothing
                return;
            }
        }
        initScenarioMap.set(scenarioId, {
            data: getFolderIdsData,
            dateRange: dateRange,
        });
        initializeCalendarsCache(scenarioId);
    }
);

orchestrator(calendarCacheInitialized, actionMessage => {
    const { calendarCacheScenarioId } = actionMessage;
    const scenarioInitVals = initScenarioMap.get(calendarCacheScenarioId);
    if (scenarioInitVals) {
        loadDataAndInitializeScenario(calendarCacheScenarioId, scenarioInitVals);
    }
});

export async function loadDataAndInitializeScenario(
    scenarioId: CalendarCacheScenarioId,
    initValues: InitValues
) {
    const { data, dateRange } = initValues;
    await updateFolderIdsAndDateRange(scenarioId, initValues);

    // trigger a calendarEventsLoaderInitialized action to notify consumers that the scenario is initialized
    const folderIds = getFolderIds(data);
    calendarEventsLoaderInitialized(folderIds, dateRange, scenarioId);
    updateIsInitializingCalendarEventsLoader(false, scenarioId);
}

export async function updateFolderIdsAndDateRange(
    scenarioId: CalendarCacheScenarioId,
    initValues: InitValues
) {
    const { data, dateRange } = initValues;
    const loadedDateRange = getLoadedDateRange(scenarioId);
    const loadedFolderIds = getLoadedFolderIds(scenarioId);
    let folderIds = getFolderIds(data);

    if (
        !areFolderIdsDifferent(folderIds, loadedFolderIds ? loadedFolderIds : []) &&
        !isDateRangeDifferent(dateRange, loadedDateRange)
    ) {
        updateLoadState('Loaded', scenarioId);
        return;
    }
    /**
     * if either the loaded folderIds or dateRange is DIFFERENT from the initialization folderIds or dateRange,
     *  re-initialize with the updated calendarIds/dateRange
     **/
    await updateFolderIdsInCalendarCache(data);
    folderIds = getFolderIds(data);
    const { canFetchAllEventsFromLocal, fetchPromise } = loadEvents(
        folderIds,
        dateRange,
        scenarioId
    );
    if (canFetchAllEventsFromLocal) {
        updateLoadState('Loading', scenarioId);
    } else {
        updateLoadState('Fetching', scenarioId);
    }
    await fetchPromise;
    updateLoadedDateRange(dateRange, scenarioId);
    updateLoadedFolderIds(folderIds, scenarioId);
    updateLoadState('Loaded', scenarioId);
}
